import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client/errors';
import { GraphQLError } from 'graphql';

export class ApiError extends Error {
  url: string;

  statusCode: number;

  bodyText: string;

  constructor(response: Response, bodyText: string) {
    super();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }

    this.name = 'ApiError';
    this.url = response.url;
    this.statusCode = response.status;
    this.bodyText = bodyText;

    try {
      const { errors } = JSON.parse(bodyText);

      this.message = errors[0];
    } catch {
      this.message = bodyText;
    }
  }
}

export class NotFoundError extends Error {
  constructor(message: string, originalError?: Error) {
    super(message);
    this.name = 'NotFoundError';
    if (originalError) {
      this.stack = originalError.stack;
    }
  }
}

// A generic graphql error. Used to wrap around Apollo Client errors so that we have an actual name
// we can use to filter on.
export class MykissApolloError extends Error {
  graphQLErrors: unknown;

  networkError: unknown;

  extraInfo: unknown;

  constructor(originalError: ErrorLike) {
    super();

    // Extract properties based on the error type
    if (CombinedGraphQLErrors.is(originalError)) {
      this.graphQLErrors = originalError.errors;
      this.extraInfo = originalError.extensions;
      this.networkError = undefined;
    } else if (ServerError.is(originalError) || ServerParseError.is(originalError)) {
      this.graphQLErrors = undefined;
      this.networkError = {
        statusCode: originalError.statusCode,
        bodyText: originalError.bodyText,
        response: originalError.response,
      };
      this.extraInfo = undefined;
    } else {
      // Generic ErrorLike (e.g., JavaScript Error, custom errors)
      // These don't have Apollo-specific properties
      this.graphQLErrors = undefined;
      this.networkError = undefined;
      this.extraInfo = undefined;
    }

    this.name = 'MykissApolloError';
    this.stack = originalError.stack;
    this.message = originalError.message;
  }
}

export class MykissApolloNotFoundError extends NotFoundError {
  graphQLErrors: unknown;

  networkError: unknown;

  extraInfo: unknown;

  constructor(message: string, originalError?: ErrorLike) {
    super(message, originalError);
    if (originalError) {
      // Extract properties based on the error type
      if (CombinedGraphQLErrors.is(originalError)) {
        this.graphQLErrors = originalError.errors;
        this.extraInfo = originalError.extensions;
        this.networkError = undefined;
      } else if (ServerError.is(originalError) || ServerParseError.is(originalError)) {
        this.graphQLErrors = undefined;
        this.networkError = {
          statusCode: originalError.statusCode,
          bodyText: originalError.bodyText,
          response: originalError.response,
        };
        this.extraInfo = undefined;
      } else {
        // Generic ErrorLike (e.g., JavaScript Error, custom errors)
        // These don't have Apollo-specific properties
        this.graphQLErrors = undefined;
        this.networkError = undefined;
        this.extraInfo = undefined;
      }
    }
    this.name = 'MykissApolloNotFoundError';
  }
}

// A generic graphql error. Used to wrap around GraphQLError so that we have an actual name we can
// use to filter on.
export class MykissGraphQLError extends Error {
  path: unknown;

  locations: unknown;

  constructor(originalError: GraphQLError) {
    super();
    this.locations = originalError.locations;
    this.path = originalError.path;
    this.name = 'MykissGraphQLError';
    this.stack = originalError.stack;
    this.message = originalError.message;
  }
}
